import React, { useMemo, useState } from 'react';
import { Layers, Plus, Search, Trash2, X } from 'lucide-react';
import { createBidPackage } from '../../services/alaCarteBidService';
import BidPricingFields, { INITIAL_BID_PRICING, buildDirectPricing } from './BidPricingFields';

/** The departments that run a bid queue — anything else has to be priced by an exec. */
const QUEUE_DEPARTMENTS = ['Marketing', 'Finance', 'HR'];

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

let customLineSeq = 0;
const newCustomLine = () => ({ key: `custom-${(customLineSeq += 1)}`, name: '', notes: '', quantity: '1' });

/**
 * Bundles several à la carte services into one bid — catalog picks, custom one-off
 * lines, or a mix. The team prices the bundle once and the client signs it as a
 * single quote.
 *
 * @param {object} props
 * @param {string} props.accountId
 * @param {string} [props.companyName]
 * @param {Array<{ id: string, name: string, icon?: string, department?: string }>} props.catalogItems
 *   Case-by-case catalog services available to bundle.
 * @param {string[]} [props.preselectedItemIds]
 * @param {boolean} [props.customOnly] Opens straight into a blank custom package.
 * @param {() => void} props.onClose
 * @param {(bidId: string) => void} props.onCreated
 */
export default function BidPackageModal({
  accountId,
  companyName = '',
  catalogItems,
  preselectedItemIds = [],
  customOnly = false,
  onClose,
  onCreated,
}) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('🧩');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('');
  const [departmentTouched, setDepartmentTouched] = useState(false);
  const [quantitiesById, setQuantitiesById] = useState(() => {
    const initial = {};
    preselectedItemIds.forEach((id) => {
      initial[id] = '1';
    });
    return initial;
  });
  const [customLines, setCustomLines] = useState(() => (customOnly ? [newCustomLine()] : []));
  const [search, setSearch] = useState('');
  const [pricing, setPricing] = useState(INITIAL_BID_PRICING);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedItems = useMemo(
    () => catalogItems.filter((item) => quantitiesById[item.id] !== undefined),
    [catalogItems, quantitiesById]
  );

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogItems;
    return catalogItems.filter(
      (item) =>
        (item.name || '').toLowerCase().includes(q) || (item.department || '').toLowerCase().includes(q)
    );
  }, [catalogItems, search]);

  /** The team the package belongs to: the most common department among picks, until an exec overrides it. */
  const effectiveDepartment = useMemo(() => {
    if (departmentTouched && department) return department;
    const counts = new Map();
    selectedItems.forEach((item) => {
      const dept = item.department || 'General';
      counts.set(dept, (counts.get(dept) || 0) + 1);
    });
    if (counts.size === 0) return department;
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }, [departmentTouched, department, selectedItems]);

  const spansDepartments = useMemo(
    () => new Set(selectedItems.map((item) => item.department || 'General')).size > 1,
    [selectedItems]
  );

  const departmentOptions = useMemo(
    () =>
      [
        ...new Set([
          ...QUEUE_DEPARTMENTS,
          ...selectedItems.map((item) => item.department || 'General'),
          effectiveDepartment,
        ]),
      ].filter(Boolean),
    [selectedItems, effectiveDepartment]
  );

  const toggleItem = (itemId) => {
    setQuantitiesById((prev) => {
      const next = { ...prev };
      if (next[itemId] === undefined) next[itemId] = '1';
      else delete next[itemId];
      return next;
    });
  };

  const setQuantity = (itemId, value) => {
    setQuantitiesById((prev) => ({ ...prev, [itemId]: value }));
  };

  const updateCustomLine = (key, field, value) => {
    setCustomLines((prev) => prev.map((line) => (line.key === key ? { ...line, [field]: value } : line)));
  };

  const filledCustomLines = customLines.filter((line) => line.name.trim());
  const totalLines = selectedItems.length + filledCustomLines.length;

  const handleCreate = async () => {
    setError('');
    if (!name.trim()) {
      setError('Give the package a name — the client sees it on their quote.');
      return;
    }
    if (totalLines === 0) {
      setError('Add at least one service to the package.');
      return;
    }
    if (!effectiveDepartment) {
      setError('Choose the team this package belongs to.');
      return;
    }
    const { directPricing, error: pricingError } = buildDirectPricing(pricing);
    if (pricingError) {
      setError(pricingError);
      return;
    }

    const items = [
      ...selectedItems.map((item) => ({
        catalogItemId: item.id,
        name: item.name,
        icon: item.icon || '📦',
        department: item.department || 'General',
        quantity: Number(quantitiesById[item.id]) || 1,
      })),
      ...filledCustomLines.map((line) => ({
        name: line.name.trim(),
        notes: line.notes.trim(),
        icon: '✏️',
        department: effectiveDepartment,
        quantity: Number(line.quantity) || 1,
      })),
    ];

    setSaving(true);
    try {
      const bidId = await createBidPackage({
        accountId,
        name: name.trim(),
        description: description.trim(),
        icon: icon.trim() || '🧩',
        department: effectiveDepartment,
        items,
        directPricing,
      });
      onCreated(bidId);
    } catch (e) {
      console.error('createBidPackage', e);
      setError(e?.message || 'Could not create the package.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Build a bid package"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6 dark:border-gray-700">
          <div className="flex-1" />
          <div className="flex items-center gap-3 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
              <Layers className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Build a bid package</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                One price, one contract{companyName ? ` for ${companyName}` : ''}
              </p>
            </div>
          </div>
          <div className="flex flex-1 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <div className="grid gap-4 sm:grid-cols-[5rem,1fr,10rem]">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Icon</label>
              <input
                type="text"
                value={icon}
                maxLength={4}
                onChange={(e) => setIcon(e.target.value)}
                className={`${inputClass} text-center text-lg`}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Package name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q4 Growth Package"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Team
              </label>
              <select
                value={effectiveDepartment}
                onChange={(e) => {
                  setDepartment(e.target.value);
                  setDepartmentTouched(true);
                }}
                className={inputClass}
              >
                <option value="">Select a team…</option>
                {departmentOptions.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {spansDepartments && effectiveDepartment && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              This package spans multiple departments. The {effectiveDepartment} team owns the whole
              bundle — change &quot;Team&quot; if another team should own it.
            </p>
          )}

          {pricing.mode === 'team' && effectiveDepartment && !QUEUE_DEPARTMENTS.includes(effectiveDepartment) && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              {effectiveDepartment} has no team bid queue — this package will only be reachable from exec
              review. Route it to Marketing, Finance, or HR if a team should price it.
            </p>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              What the client asked for <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Context for the team pricing this package"
              className={inputClass}
            />
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Catalog services
                {selectedItems.length > 0 && (
                  <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-200">
                    {selectedItems.length} selected
                  </span>
                )}
              </h4>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search services…"
                  className={`${inputClass} w-56 pl-9`}
                />
              </div>
            </div>

            {filteredCatalog.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                No catalog services match.
              </p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                {filteredCatalog.map((item) => {
                  const selected = quantitiesById[item.id] !== undefined;
                  return (
                    <li key={item.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <input
                        type="checkbox"
                        id={`pkg-item-${item.id}`}
                        checked={selected}
                        onChange={() => toggleItem(item.id)}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <label htmlFor={`pkg-item-${item.id}`} className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="text-lg leading-none">{item.icon || '📦'}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                            {item.name}
                          </span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">{item.department}</span>
                        </span>
                      </label>
                      {selected && (
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={quantitiesById[item.id]}
                          onChange={(e) => setQuantity(item.id, e.target.value)}
                          aria-label={`Quantity for ${item.name}`}
                          className="w-16 shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
              Custom services <span className="font-normal text-gray-500">— not in the catalog</span>
            </h4>
            {customLines.length > 0 && (
              <ul className="mb-2 space-y-2">
                {customLines.map((line) => (
                  <li key={line.key} className="flex items-start gap-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={line.quantity}
                      onChange={(e) => updateCustomLine(line.key, 'quantity', e.target.value)}
                      aria-label="Quantity"
                      className="w-16 shrink-0 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <input
                        type="text"
                        value={line.name}
                        onChange={(e) => updateCustomLine(line.key, 'name', e.target.value)}
                        placeholder="Service name"
                        className={inputClass}
                      />
                      <input
                        type="text"
                        value={line.notes}
                        onChange={(e) => updateCustomLine(line.key, 'notes', e.target.value)}
                        placeholder="Details for the team (optional)"
                        className={inputClass}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setCustomLines((prev) => prev.filter((l) => l.key !== line.key))}
                      className="rounded p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      aria-label="Remove custom service"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setCustomLines((prev) => [...prev, newCustomLine()])}
              className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400"
            >
              <Plus className="h-4 w-4" />
              Add custom service
            </button>
          </div>

          <BidPricingFields pricing={pricing} onChange={setPricing} team={effectiveDepartment} />

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {totalLines} {totalLines === 1 ? 'service' : 'services'} in this package
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              <Layers className="h-4 w-4" />
              {saving
                ? 'Creating…'
                : pricing.mode === 'direct'
                  ? pricing.publish
                    ? 'Create & publish package'
                    : 'Save priced package'
                  : 'Create package & send out for bid'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
